import { useRef } from "react";
import { motion, useTransform, useMotionValue } from "framer-motion";


/* ------------------------------------------------------------------ */
/*  The recurring visual research thread: two trajectories that begin  */
/*  together and gradually develop a visible distance between them.    */
/* ------------------------------------------------------------------ */

const TECH_PTS = [
  [40, 300],
  [170, 283],
  [300, 228],
  [430, 160],
  [560, 100],
];
const HUMAN_PTS = [
  [40, 300],
  [170, 301],
  [300, 300],
  [430, 294],
  [560, 282],
];

function Connector({ i, progress, delay, assemble }) {
  const fallback = useMotionValue(1);
  const prog = progress || fallback;
  const start = 0.25 + i * 0.18;
  const opacity = useTransform(prog, [start, start + 0.12], [0, 1]);
  const [x1, y1] = TECH_PTS[i];
  const [x2, y2] = HUMAN_PTS[i];
  if (assemble) {
    return (
      <motion.line
        x1={x1} y1={y1} x2={x2} y2={y2}
        stroke="#1E4D38" strokeWidth="1" strokeDasharray="3 4" strokeOpacity="0.55"
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ duration: 0.8, delay, ease: "easeOut" }}
      />
    );
  }
  return (
    <motion.line
      x1={x1} y1={y1} x2={x2} y2={y2}
      stroke="#1E4D38" strokeWidth="1" strokeDasharray="3 4" strokeOpacity="0.55"
      style={{ opacity }}
    />
  );
}

function NodeDot({ x, y, i, progress, assemble, delay, fill }) {
  const fallback = useMotionValue(1);
  const prog = progress || fallback;
  const opacity = useTransform(prog, [i * 0.12, i * 0.12 + 0.1], [0.12, 1]);
  return (
    <motion.circle
      cx={x} cy={y} r={i === 0 ? 3 : 2.5}
      fill={fill}
      initial={assemble ? { scale: 0, opacity: 0 } : undefined}
      animate={assemble ? { scale: 1, opacity: 1 } : undefined}
      transition={assemble ? { duration: 0.4, delay } : undefined}
      style={!assemble ? { opacity } : undefined}
    />
  );
}

export function DisequilibriumVisual({ progress = null, assemble = false, showLabels = false, showGap = false, delay = 0, className = "" }) {
  const fallback = useMotionValue(1);
  const prog = progress || fallback;
  const pathProgress = useTransform(prog, (v) => Math.min(1, v * 1.4));
  const gapOpacity = useTransform(prog, [0.75, 0.95], [0, 1]);

  const pathProps = assemble
    ? { initial: { pathLength: 0 }, animate: { pathLength: 1 } }
    : { style: { pathLength: pathProgress } };

  return (
    <svg viewBox="0 0 640 400" className={className} role="img" aria-label="Two trajectories gradually developing a distance between them">
      {[100, 200, 300].map((y) => (
        <line key={y} x1="0" y1={y} x2="640" y2={y} stroke="#0F0F0F" strokeOpacity="0.05" strokeWidth="1" />
      ))}

      {showGap && (
        <motion.polygon
          points="430,160 560,100 560,282 430,294"
          fill="#1E4D38"
          fillOpacity="0.06"
          initial={assemble ? { opacity: 0 } : undefined}
          animate={assemble ? { opacity: 1 } : undefined}
          transition={assemble ? { duration: 1, delay: delay + 1.2 } : undefined}
          style={!assemble && gapOpacity ? { opacity: gapOpacity } : undefined}
        />
      )}

      {/* human adaptive capacity: near-flat trajectory */}
      <motion.path
        d="M40,300 C160,302 300,300 560,282"
        fill="none" stroke="#0F0F0F" strokeOpacity="0.6" strokeWidth="2"
        {...pathProps}
        transition={assemble ? { duration: 1.2, delay: delay + 0.2, ease: [0.22, 1, 0.36, 1] } : undefined}
      />
      {/* technological expansion: rising trajectory */}
      <motion.path
        d="M40,300 C160,286 300,228 560,100"
        fill="none" stroke="#1E4D38" strokeWidth="2"
        {...pathProps}
        transition={assemble ? { duration: 1.4, delay: delay + 0.45, ease: [0.22, 1, 0.36, 1] } : undefined}
      />

      {TECH_PTS.map(([x, y], i) => (
        <NodeDot key={`t${i}`} x={x} y={y} i={i} progress={progress} assemble={assemble} delay={delay + 0.3 + i * 0.18} fill="#1E4D38" />
      ))}
      {HUMAN_PTS.map(([x, y], i) => (
        <NodeDot key={`h${i}`} x={x} y={y} i={i} progress={progress} assemble={assemble} delay={delay + 0.15 + i * 0.18} fill="#0F0F0F" />
      ))}

      {[1, 2, 3, 4].map((i) => (
        <Connector key={i} i={i} progress={progress} assemble={assemble} delay={delay + 1.0 + i * 0.15} />
      ))}

      {showLabels && (
        <>
          <text x="556" y="84" textAnchor="end" fontSize="10" letterSpacing="2" fill="#1E4D38" style={{ textTransform: "uppercase" }}>
            TECHNOLOGICAL EXPANSION
          </text>
          <text x="556" y="308" textAnchor="end" fontSize="10" letterSpacing="2" fill="#6B6A65" style={{ textTransform: "uppercase" }}>
            HUMAN ADAPTIVE CAPACITY
          </text>
          {showGap && (
            <text x="492" y="205" textAnchor="middle" fontSize="10" letterSpacing="2" fill="#1E4D38" style={{ textTransform: "uppercase" }}>
              DISEQUILIBRIUM
            </text>
          )}
        </>
      )}
    </svg>
  );
}

/* Signals opening outward: the inquiry continues */
export function BranchVisual({ className = "" }) {
  const ref = useRef(null);
  return (
    <svg ref={ref} viewBox="0 0 220 120" className={className} aria-hidden="true">
      <motion.line x1="10" y1="60" x2="90" y2="60" stroke="#0F0F0F" strokeOpacity="0.5" strokeWidth="1.5"
        initial={{ pathLength: 0 }} whileInView={{ pathLength: 1 }} viewport={{ once: true }} transition={{ duration: 0.8 }} />
      <motion.circle cx="90" cy="60" r="3.5" fill="#1E4D38"
        initial={{ scale: 0 }} whileInView={{ scale: 1 }} viewport={{ once: true }} transition={{ duration: 0.4, delay: 0.7 }} />
      {[
        [210, 18],
        [210, 60],
        [210, 102],
      ].map(([x2, y2], i) => (
        <motion.line key={i} x1="90" y1="60" x2={x2} y2={y2}
          stroke="#1E4D38" strokeWidth="1" strokeDasharray="3 4" strokeOpacity="0.6"
          initial={{ pathLength: 0 }} whileInView={{ pathLength: 1 }} viewport={{ once: true }}
          transition={{ duration: 0.9, delay: 0.9 + i * 0.2, ease: [0.22, 1, 0.36, 1] }} />
      ))}
      {[
        [210, 18],
        [210, 60],
        [210, 102],
      ].map(([x, y], i) => (
        <motion.circle key={`n${i}`} cx={x} cy={y} r="2.5" fill="#0F0F0F" fillOpacity="0.55"
          initial={{ scale: 0 }} whileInView={{ scale: 1 }} viewport={{ once: true }}
          transition={{ duration: 0.35, delay: 1.6 + i * 0.2 }} />
      ))}
    </svg>
  );
}
